<template>
  <div class="main-content wide with-sidebar">
    <div class="wrap">
      <sidebar-left />
      <div class="page-main-block">
        <h1>{{ pageData.pageTitle }}</h1>
        <div
          v-dompurify-html="pageData.description"
          class="description"
        />
        <search-wrapper
          :placeholder-pesktop="pageData.placeholder"
          :placeholder-mobile="pageData.placeholderMobile"
          @filterData="filterData"
        />
        <div
          v-if="Object.keys(tableData).length"
          class="table-block"
        >
          <table
            v-if="!isMobile"
            class="table-wrap"
          >
            <tr
              v-if="Object.keys(tableData.header).length"
              class="table-header table-row"
            >
              <th class="table-cell header-cell">
                {{ tableData.header[0] + '/' + tableData.header[1] }}
              </th>
              <th
                v-for="(cell, cellIndex) in tableData.header.slice(2)"
                :key="cellIndex"
                v-dompurify-html="cell"
                class="table-cell header-cell"
              />
            </tr>

            <tr
              v-for="(row, rowIndex) in chunkedList"
              :key="rowIndex"
              class="table-row"
            >
              <td class="table-cell">
                <div v-dompurify-html="row[0]" />
                <div
                  v-dompurify-html="row[1]"
                  class="light"
                />
              </td>
              <td
                v-for="(cell, cellIndex) in row.slice(2)"
                :key="cellIndex"
                class="table-cell"
              >
                <span
                  v-if="showCheckIcon(cell)"
                  class="icon check"
                ></span>
                <span
                  v-else-if="showRejectIcon(cell)"
                  class="icon reject"
                ></span>
                <template v-else>
                  <span v-html="cell"></span>
                </template>
              </td>
            </tr>
            <tr
              v-if="!showAll && tableData.rows.length > pageData.visibleItems"
              class="table-row last"
              :class="{ gradient: pageData.visibleItems % 2 === 1 }"
            >
              <td :colspan="Object.keys(tableData.header).length - 1">
                <div class="btn btn-ghost cta">
                  <a
                    href="#"
                    @click.prevent="showAllAction"
                  >
                    {{ $t('reusable.showAll') }}
                  </a>
                </div>
              </td>
            </tr>
          </table>

          <div v-if="isMobile">
            <div
              v-for="(row, index) in chunkedList"
              :key="index"
              class="table-row"
            >
              <div class="header-cell">
                <span v-dompurify-html="row[0]" />
                <span v-if="row[0] && row[1]"> - </span>
                <span
                  v-dompurify-html="row[1]"
                  class="light"
                />
              </div>
              <div
                v-for="(cell, cellIndex) in row.slice(2)"
                :key="cellIndex"
                class="table-cell"
              >
                <div
                  v-dompurify-html="tableData.header[cellIndex + 2]"
                  class="left-col"
                />
                <div class="right-col">
                  <span v-if="showCheckIcon(cell) || showRejectIcon(cell)">
                    {{ $t(cell) }}
                  </span>
                  <span
                    v-else
                    v-dompurify-html="cell"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="!showAll && tableData.rows.length > pageData.visibleItems"
              class="'table-row last'"
              :class="{ gradient: pageData.visibleItems % 2 === 1 }"
            >
              <div class="btn btn-ghost cta">
                <a
                  href="#"
                  @click.prevent="showAllAction"
                >
                  {{ $t('reusable.showAll') }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import SidebarLeft from 'components/Sidebar/Sidebar'
  import SearchWrapper from 'components/Tables/SearchWrapper'

  export default {
    components: {
      SidebarLeft,
      SearchWrapper
    },
    data() {
      return {
        showAll: false
      }
    },
    computed: {
      ...mapGetters('device', ['isMobile']),
      ...mapState('searchTable', ['pageData', 'tableData']),
      chunkedList() {
        return this.showAll || this.tableData.rows < this.pageData.visibleItems
          ? this.tableData.rows
          : this.tableData.rows.slice(0, this.pageData.visibleItems)
      }
    },
    methods: {
      filterData(value) {
        this.$store.dispatch('searchTable/filterData', value)
      },
      showAllAction() {
        this.showAll = true
      },
      showCheckIcon(cell) {
        if (!cell) {
          return false
        }
        const formattedCell = cell.toString().toLowerCase()

        return (
          formattedCell === 'table.yes' ||
          formattedCell === 'table.included' ||
          formattedCell === 'table.available'
        )
      },

      showRejectIcon(cell) {
        if (!cell) {
          return false
        }
        const formattedCell = cell.toString().toLowerCase()
        return (
          formattedCell === 'table.no' ||
          formattedCell === 'table.notincluded' ||
          formattedCell === 'table.notavailable'
        )
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .with-sidebar {
    padding: 26px 0 0;

    @include bp-tablet-only {
      padding: 45px 0 20px;
    }

    @include bp-mf-desktop {
      padding: 45px 0 72px;
    }

    h1 {
      margin: 2px 0 25px;
      padding: 0 23px;
      font-size: em(40);
      font-weight: 300;

      @include bp-tablet {
        margin: 0 0 11px;
      }

      @include bp-mobile {
        margin-bottom: 12px;
        font-size: em(32);
        word-wrap: break-word;
      }

      @include bp-mf-tablet {
        margin: 0 0 12px;
        padding: 0;
        font-size: em(40);
      }
    }

    .wrap {
      display: flex;
      flex-direction: column;
      @include clearfix();

      @include bp-mf-desktop {
        display: block;
      }
    }

    .description {
      ::v-deep p {
        padding: 0 23px;
        margin: 0 0 25px;
        line-height: 24px;
        font-size: em(16);
        font-weight: 300;

        .js-inline-button {
          font-size: em(15, 16);
        }

        @include bp-mf-tablet {
          padding: 0;
          margin-bottom: 32px;
          font-size: em(18);
          line-height: 27px;

          .js-inline-button {
            font-size: em(15, 18);
          }
        }

        @include bp-mf-desktop {
          margin-bottom: 28px;
          font-size: em(20);

          .js-inline-button {
            font-size: em(15, 20);
          }
        }
      }

      ::v-deep b,
      ::v-deep strong {
        font-weight: 500;
      }
    }
  }

  .page-main-block {
    order: 1;

    @include bp-tablet-only {
      padding: 0 26px;
    }

    @include bp-mf-desktop {
      padding: 0;
      float: left;
      vertical-align: top;
      width: 66%;
    }

    ::v-deep a:not(.js-inline-button) {
      color: $blue-medium;

      &:hover {
        color: $blue-light;
      }
    }
  }

  .table-wrap {
    width: 100%;
  }

  .table-header {
    background: $gray-yellow;
  }

  .table-row {
    padding: 20px 23px;
    white-space: pre-line;

    &:nth-child(2n) {
      background-color: $table-gray-background;
    }

    &.last {
      background-color: transparent;

      td {
        text-align: center;
      }
    }

    &.gradient {
      background: linear-gradient(180deg, $table-gray-background 0%, #ffffff 100%);
    }
  }

  td {
    padding: 16px 0;
  }

  th {
    padding: 13px 0;
    font-weight: 500;
  }

  th,
  td {
    &:first-child {
      text-align: left;
      padding-left: 18px;
    }

    &:last-child {
      padding-right: 18px;
    }
  }

  .table-cell {
    margin-top: 12px;
    @include clearfix();

    @include bp-mf-tablet {
      margin: 0;

      &:not(:first-child) {
        padding-left: 10px;
        text-align: center;
      }

      &:first-child {
        font-weight: 500;
      }
    }

    .light {
      margin-top: 5px;
      color: $grey;
    }
  }

  .header-cell {
    font-weight: 500;
    line-height: 16px;

    .light {
      color: $grey;
    }
  }

  .left-col {
    width: 50%;
    color: $grey;
    float: left;
  }

  .right-col {
    width: 50%;
    float: left;
    text-align: right;
  }

  .icon {
    @include line-icon-font();
    font-size: 16px;

    &.reject {
      &:before {
        content: $line-icon-block;
      }
    }

    &.check {
      &:before {
        content: $line-icon-check-circle;
        color: $blue-medium;
      }
    }
  }

  .terms-wrap {
    padding: 40px 24px;

    @include bp-mf-tablet {
      padding: 48px 0;
    }
  }

  .cta {
    font-size: em(14);
    line-height: 40px;
    align-self: center;
    font-weight: 700;
    border: 1px solid $gray-border;
    width: auto;

    a {
      font-weight: 700;
    }
  }
</style>
